import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    FluentProvider,
    Link,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { McpSupportSectionLinks } from '../../../constants/mcp-server';
import mcpPromotionIllustration from '../../../content/images/mcp-illustration.webp';
import { useMcpPromotionDialogContext } from '../../../hooks/context/dialogs';
import { useCurrentFluent2Theme } from '../../../hooks/styling';
import { openInNewWindow } from '../../../utilities/browser';
import { McpPromotionDialogContextProperties } from './mcp-promotion-dialog-context';

export interface McpPromotionDialogComponentProps extends McpPromotionDialogContextProperties {
    open?: boolean;
    onDismiss: () => void;
}

const messages = defineMessages({
    closeButtonAriaLabel: {
        id: 'McpPromotionDialog_CloseButton_AriaLabel',
        defaultMessage: 'Close',
        description: 'Aria label for the "x" button in the top corner of the MCP promotion dialog',
    },
    actionButtonAriaLabel: {
        id: 'McpPromotionDialog_ActionButton_AriaLabel',
        defaultMessage: 'Install in VS Code',
        description: 'Aria label for the action/primary action button',
    },
    tryLaterButtonAriaLabel: {
        id: 'McpPromotionDialog_TryLaterButton_AriaLabel',
        defaultMessage: 'Dismiss and try later',
        description: 'Aria label for the try later button',
    },
});

/**
 * Styles
 */
const dialogWidth = '600px';
const dialogStyle: React.CSSProperties = {
    width: dialogWidth,
};

const dialogBodyStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
};

const imageContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '240px',
};

const titleContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
};

const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
};

const promotionImageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
};

const messageContainerStyle: React.CSSProperties = {
    padding: '4px 0 8px 0',
    flex: '1',
};

const actionsStyle: React.CSSProperties = {
    justifyContent: 'flex-end',
    paddingBottom: 0,
};

/*
 * END Styles
 */

const McpPromotionDialog: React.FC<McpPromotionDialogComponentProps> = (props: McpPromotionDialogComponentProps) => {
    const { open, onDismiss } = props;

    // Theming hooks
    const theme = useCurrentFluent2Theme();

    // Intl hooks
    const { formatMessage } = useIntl();

    // Event handlers
    const handleActionButtonClick = React.useCallback(() => {
        openInNewWindow(McpSupportSectionLinks.VscodeInstallation);
        onDismiss();
    }, [onDismiss]);

    return (
        <FluentProvider theme={theme}>
            <Dialog modalType="alert" open={open} onOpenChange={onDismiss}>
                <DialogSurface style={dialogStyle}>
                    <DialogBody style={dialogBodyStyle}>
                        {/* Image Section */}
                        <div style={imageContainerStyle}>
                            <img src={mcpPromotionIllustration} alt="" style={promotionImageStyle} />
                        </div>

                        <DialogContent>
                            <div style={titleContainerStyle}>
                                <DialogTitle style={titleStyle}>
                                    <FormattedMessage
                                        id="McpPromotionDialog_Title_Text"
                                        defaultMessage="Dev box MCP Server is now available!"
                                        description="Title for the MCP promotion dialog"
                                    />
                                </DialogTitle>
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label={formatMessage(messages.closeButtonAriaLabel)}
                                        icon={<Dismiss24Regular />}
                                    />
                                </DialogTrigger>
                            </div>
                            <div style={messageContainerStyle}>
                                <FormattedMessage
                                    id="McpPromotionDialog_Message_Text"
                                    defaultMessage="Discover our new Model Context Protocol integration that provides seamless integration between AI agents and Microsoft Dev Box services."
                                    description="Message content for the MCP promotion dialog"
                                />
                                {' ' /* Space for better readability */}
                                <Link href={McpSupportSectionLinks.LocalMcpPackage} target="_blank">
                                    <FormattedMessage
                                        id="McpPromotionDialog_LearnMoreLink_Text"
                                        defaultMessage="Learn more"
                                        description="Text for the learn more link in the MCP promotion dialog"
                                    />
                                </Link>
                            </div>
                        </DialogContent>
                        <DialogActions style={actionsStyle}>
                            <Button
                                appearance="primary"
                                aria-label={formatMessage(messages.actionButtonAriaLabel)}
                                onClick={handleActionButtonClick}
                            >
                                <FormattedMessage
                                    id="McpPromotionDialog_ActionButton_Text"
                                    defaultMessage="Install in VS Code"
                                    description="Text for the action/primary action button"
                                />
                            </Button>
                            <DialogTrigger action="close">
                                <Button
                                    appearance="secondary"
                                    aria-label={formatMessage(messages.tryLaterButtonAriaLabel)}
                                >
                                    <FormattedMessage
                                        id="McpPromotionDialog_TryLaterButton_Text"
                                        defaultMessage="Try later"
                                        description="Text for the try later button"
                                    />
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </FluentProvider>
    );
};

export default McpPromotionDialog;

export const McpPromotionDialogContextWrapper: React.FC = () => {
    // Context hooks
    const { isOpen, closeSurface, properties } = useMcpPromotionDialogContext();

    if (!isOpen || !properties) {
        return <></>;
    }

    return <McpPromotionDialog {...properties} open={isOpen} onDismiss={closeSurface} />;
};
